import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getContactStatusSuffix } from '../../views/contacts/contactTypeOptions';
import { useSettings } from 'services/settings/SettingsContext';
import { useFetchData } from 'services/api/useFetchData';
import { FieldType } from 'types/FieldTypes';
import FormModal from 'components/modals/FormModal';
import ContactForm from '../../views/contacts/ContactForm';
import LocationForm from '../../views/contacts/LocationForm';
import PersonForm from '../../views/contacts/PersonForm';
import AssetForm from '../../views/assets/AssetForm';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useActiveFeature } from 'services/permissions/permissionChecks';
import { Division } from '../environments/EnvironmentTypes';
import JobCreationDetailsPanel from './JobCreationDetailsPanel';

// Rename the name of the function:
const JobForm: React.FC<{
    viewKey?: string;
    linkedItem?: { [key: string]: any };
}> = ({
    viewKey,
    linkedItem,
}) => {
    const { t } = useTranslation();
    const { userPreferences } = useSettings();
    const { activeFeatures, userDivision } = useAuthContext();

    // Determine if the job creation details side tab may be shown
    const hasJobCreationDetails = useActiveFeature('job_creation_details');

    // Determine whether equipment can be scheduled on the appointment
    const equipmentSchedulingActive = useActiveFeature('equipment_scheduling');

    // Set the api object
    const apiObject = 'job';

    // Get the appointment colors setting
    const { scheduling_color_mode: scheduling_appointment_colors } = userPreferences;

    // Fetch the divisions, to group and restrict the job type options
    const hasDivisionsFeature = activeFeatures.includes('divisions');
    const { response: divisionsResponse } = useFetchData({
        apiUrl: hasDivisionsFeature ? 'get_division_list' : '',
        params: useMemo(() => ({ is_active: true }), []),
    });
    const divisions: Division[] = divisionsResponse?.results ?? [];

    // Configure the job fields
    const fields: FieldType[] = [
        { 
            type: 'searchselect', 
            name: 'client',
            label: 'job.general.client_label',
            placeholder: 'job.general.client_placeholder',
            objectName: 'client.general.object_name.singular',
            query: {
                // Get all contacts which have the contact type 'contact', 'lead' and 'client'
                endpoint: 'get_contact_search',
                params: {
                    contact_type: ['contact', 'lead', 'client'] 
                }
            },
            selectionFormat: 'name',
            selectionSuffix: (option) => option.blocked ? `(${t('contact.general.blocked_suffix')})` : null,
            optionFormat: {
                title: { field: 'name', format: (value) => value },
                bracket: { field: 'contact_type', format: (value, option) => getContactStatusSuffix(option, value) ?? '', optional: false },
                subtitle: { field: 'primary_location', format: (value) => value.address, optional: true},
            },
            storeWholeObject: true,
            addNewItem: { 
                method: 'modal-form', 
                modalForm: <ContactForm />,
                linkedItem: { contact_type: 'client' },
                onSuccess: (response, { setUpdatedData }) => {
                    setUpdatedData(prev => {
                        let updated = { ...prev };

                        // Set the newly added person of the organization, as location person
                        if (response.entity_type === 'organization' && response.primary_person) {
                            if (!prev.location_person) {
                                updated.location_person = {
                                    id: response.primary_person.id,
                                    value: response.primary_person.id,
                                    name: response.primary_person.name,
                                    ...response.primary_person
                                };
                            }
                        }

                        // Reset the location person value, if the selected client is a person
                        if (response.entity_type === "person") {
                            updated.location_person = undefined;
                        }

                        // Set the newly added location, as work location
                        if (response.primary_locations && response.primary_locations.length > 0) {
                            if (!prev.work_location) {
                                const newLocation = response.primary_locations[0];
                                updated.work_location = {
                                    id: newLocation.id,
                                    value: newLocation.id,
                                    address: newLocation.address,
                                    ...newLocation
                                };
                            }
                        }

                        return updated;
                    });
                }
            },
        },
        // {
        //     type: 'client-internal-note',
        //     name: 'client_internal_note',
        //     label: 'job.general.important_client_note',
        //     placeholder: 'job.general.internal_note_placeholder',
        //     dependentOn: { fieldName: 'client' }
        // },
        { 
            type: 'searchselect', 
            name: 'work_location',
            label: 'job.general.work_location_label',
            placeholder: 'job.general.work_location_placeholder',
            objectName: 'location.general.object_name.singular',
            query: { endpoint: 'get_location_search' },
            showOptionsOnClick: (updatedData) => {
                if (!updatedData.client?.id) return null;
                return { endpoint: `get_location_search/${updatedData.client.id}` };
            },
            selectionFormat: 'address',
            optionFormat: { title: { field: 'address', format: (value) => value }},
            addNewItem: { method: 'modal-form', modalForm: <LocationForm /> },
            directOptionsTitle: 'job.general.locations_of_client_label',
        },
        {
            type: 'multi-searchselect',
            name: 'assets',
            label: 'job.general.assets_label',
            placeholder: 'job.general.assets_placeholder',
            tooltipText: 'job.general.assets_tooltip',
            objectName: 'asset.general.object_name.singular',
            activeFeature: 'asset_management',
            query: (updatedData) => {
                const workLocationId = updatedData?.work_location?.id ?? updatedData?.work_location;
                if (workLocationId) {
                    return { endpoint: `get_asset_search/${workLocationId}` };
                }
                return { endpoint: 'get_asset_search' };
            },
            showOptionsOnClick: (updatedData) => {
                const workLocationId = updatedData?.work_location?.id ?? updatedData?.work_location;
                if (!workLocationId) return null;
                return { endpoint: `get_asset_search/${workLocationId}` };
            },
            directOptionsTitle: 'asset.general.assets_of_location_label',
            selectionFormat: 'name',
            optionFormat: {
                title: { field: 'name', format: (value) => value },
                subtitle: { field: 'number', format: (value) => value, optional: true },
            },
            specialOptions: [
                { value: 'all', label: 'asset.general.all_assets_option', dataField: 'all_assets' },
            ],
            addNewItem: {
                method: 'modal-form',
                modalForm: <AssetForm />,
                linkedItem: (updatedData) => {
                    const workLocationId = updatedData?.work_location?.id ?? updatedData?.work_location;
                    return workLocationId ? { location: workLocationId } : undefined;
                },
            },
        },
        {
            type: 'searchselect',
            name: 'location_person',
            label: 'job.general.contact_at_location_long_label',
            placeholder: 'job.general.contact_at_location_placeholder',
            objectName: 'person.general.object_name.singular',
            query: { endpoint: 'get_person_search' },
            showOptionsOnClick: (updatedData) => {
                if (!updatedData.client?.id) return null;
                return { endpoint: `get_person_search/${updatedData.client.id}` };
            },
            selectionFormat: 'name',
            optionFormat: { title: { field: 'name', format: (value) => value } },
            addNewItem: { method: 'modal-form', modalForm: <PersonForm /> },
            dependentOn: [
                { fieldName: 'client.entity_type', notValue: 'person' },
            ],
            directOptionsTitle: 'job.general.persons_of_client_label'
        },
        {
            type: 'header',
        },
        ...(activeFeatures.includes('projects') ? [
            { 
                type: 'searchselect', 
                name: 'project',
                label: 'job.general.project_label',
                placeholder: 'job.general.project_placeholder',
                objectName: 'project.general.object_name.singular',
                query: { endpoint: 'get_project_search' },
                showOptionsOnClick: (updatedData) => {
                    if (!updatedData.client?.id) return null;
                    return { endpoint: `get_project_search/${updatedData.client.id}` };
                },
                disableAddNew: true,
                selectionFormat: 'name',
                optionFormat: {
                    title: { field: 'name', format: (value) => value },
                    subtitle: { field: 'number', format: (value) => value, optional: true},
                },
                directOptionsTitle: 'job.general.projects_of_client_label',
            } as FieldType] : []
        ),
        {
            type: 'dropdown',
            name: 'division',
            label: 'job.general.division_label',
            apiRequest: [{
                endpoint: 'get_division_list',
                object: 'division',
                params: { is_active: true },
                useCache: true
            }],
            disabled_selected: 'job.general.division_disabled_selected',
            activeFeature: 'divisions',
            defaultValue: userDivision ? { value: String(userDivision) } : undefined,
            allowNoneOption: false,
            showSearch: false,
        },
        {
            type: 'dropdown',
            name: 'workflow',
            label: 'job.general.type_label',
            apiRequest: [{
                endpoint: 'get_workflow_list',
                object: 'workflow',
                params: { is_active: true, connected_feature: 'job' },
                useCache: true
            }],
            disabled_selected: 'job.general.type_disabled_selected',
            activeFeature: 'job_types',
            allowNoneOption: false,
            showSearch: false,
            orderOptions: true,
            groupByField: 'division',
            formatGroupTitle: (group) => divisions.find(division => String(division.id) === group)?.name || group,
            restrictGroupToField: 'division',
        },
        {
            type: 'textarea',
            name: 'main_description',
            label: 'job.general.main_description_label',
            rows: 3,
            placeholder: 'job.general.main_description_placeholder',
        },
        {
            type: 'header',
        },
        {
            type: 'dropdown', 
            name: 'plan_method', 
            label: 'job.general.plan_method_label', 
            disabled_selected: 'job.general.plan_method_disabled_selected',
            hardcodedOptions: [
                { value: 'add_to_storage', name: 'job.plan_method.add_to_storage'},
                { value: 'direct_schedule', name: 'job.plan_method.direct_schedule'},
            ],
            defaultValue: { value: 'add_to_storage' },
            allowNoneOption: false,
            showSearch: false,
        },
        
        // Add to storage fields. Don't render them on direct schedule
        {
            type: 'date',
            name: 'target_date',
            label: 'job.general.target_date_label',
            width: '50%',
            group: 'field-row',
            dependentOn: { fieldName: 'plan_method', value: 'add_to_storage' },
        },
        {
            type: 'text',
            name: 'estimated_duration',
            label: 'job.general.estimated_duration_label',
            placeholder: 'job.general.estimated_duration_placeholder',
            width: '50%',
            group: 'field-row',
            dependentOn: { fieldName: 'plan_method', value: 'add_to_storage' },
        },

        // Direct scheduling fields
        {
            type: 'date',
            name: 'appointment_date',
            label: 'job.schedule_job.date_label',
            width: '40%',
            group: 'field-row',
            dependentOn: { fieldName: 'plan_method', value: 'direct_schedule' },
        },
        {
            type: 'time',
            name: 'appointment_start_time',
            label: 'job.schedule_job.start_time_label',
            width: '30%',
            group: 'field-row',
            dependentOn: { fieldName: 'plan_method', value: 'direct_schedule' },
        },
        {
            type: 'time',
            name: 'appointment_end_time',
            label: 'job.schedule_job.end_time_label',
            width: '30%',
            group: 'field-row',
            dependentOn: { fieldName: 'plan_method', value: 'direct_schedule' },
        },
        { 
            type: 'multiselect', 
            name: 'appointment_assignees', 
            label: 'job.schedule_job.assignees_label',
            apiObject: 'user',
            apiRequest: [{
                endpoint: `get_user_list`,
                object: 'user',
                params: { is_active: true }
            }],
            disabled_selected: 'job.schedule_job.assignees_disabled_selected',
            backendField: 'user_hash',
            allowNoneOption: false,
            optionFormat: (option) => option.full_name,
            selectionFormat: 'full_name',
            params: {
                is_active: true,
            },
            dependentOn: { fieldName: 'plan_method', value: 'direct_schedule' },
        },
        // Only offer the equipment when it can be scheduled in this environment
        ...(equipmentSchedulingActive ? [{
            type: 'multiselect',
            name: 'appointment_equipment',
            label: 'job.schedule_job.equipment_label',
            apiObject: 'equipment',
            apiRequest: [{
                endpoint: 'get_equipment_list',
                object: 'equipment',
                params: { is_active: true }
            }],
            disabled_selected: 'job.schedule_job.equipment_disabled_selected',
            allowNoneOption: false,
            optionFormat: (option: any) => option.type_name ? `${option.name} (${option.type_name})` : option.name,
            selectionFormat: 'name',
            params: {
                is_active: true,
            },
            dependentOn: { fieldName: 'plan_method', value: 'direct_schedule' },
        } as FieldType] : []),
        ...(scheduling_appointment_colors === "custom" ? [
            { 
                name: 'appointment_custom_color',
                label: 'job.schedule_job.color_label',
                type: 'tabs',
                showIconOnly: true,
                customClass: "label",
                options: [
                    { value: "soft-dark-blue", name: "soft-dark-blue" },
                    { value: "soft-blue", name: "soft-blue" },
                    { value: "soft-dark-green", name: "soft-dark-green" },
                    { value: "soft-green", name: "soft-green" },
                    { value: "soft-yellow", name: "soft-yellow" },
                    { value: "soft-orange", name: "soft-orange" },
                    { value: "soft-red", name: "soft-red" },
                    { value: "soft-gray", name: "soft-gray" },
                    { value: "soft-dark-gray", name: "soft-dark-gray" },
                    { value: "soft-purple", name: "soft-purple" },
                ],
                dependentOn: { fieldName: 'plan_method', value: 'direct_schedule' },
            } as FieldType] : []
        ),
    ];
    
    return (
        <FormModal
            viewKey={viewKey}
            apiObject={apiObject}
            fields={fields}
            linkedItem={linkedItem}
            sidePanel={hasJobCreationDetails ? <JobCreationDetailsPanel /> : undefined}
        />
    );
};

export default JobForm;